import Decimal from 'decimal.js';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  Receipt,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Badge,
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/shadcn/index.js';
import { EmptyState } from '../../components/ui/index.js';
import { formatDate, formatDateTime, formatNumber } from '../../lib/intl.js';
import { narrowUsd } from '../../lib/money.js';
import { platformApi } from '../../lib/platformApi.js';
import { KpiCard } from '../reports/_shared/KpiCard.js';

import { TenantLogo } from './TenantLogo.js';

type BillingStatus = 'trialing' | 'active' | 'past_due' | 'unpaid' | 'cancelled';

interface TenantListItem {
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan: 'free' | 'starter' | 'pro' | 'enterprise';
    logoUrl: string | null;
    billingStatus: BillingStatus;
    currentPeriodEnd: string | null;
    trialEndsAt: string | null;
    lastPaymentAt: string | null;
    yearlyPriceAmount: string | null;
    yearlyPriceCurrencyCode: string | null;
  };
  userCount: number;
}

interface AuditEntry {
  id: string;
  platformAdminId: string;
  action: string;
  targetTenantId: string | null;
  diff: unknown;
  createdAt: string;
}

interface PaymentDiff {
  before?: { billingStatus?: string; currentPeriodEnd?: string | null; lastPaymentAt?: string | null };
  after?: { billingStatus?: string; currentPeriodEnd?: string | null; lastPaymentAt?: string | null };
  years?: number;
  note?: string | null;
}

const BILLING_VARIANT: Record<BillingStatus, 'info' | 'success' | 'warning' | 'danger' | 'secondary'> = {
  trialing: 'info',
  active: 'success',
  past_due: 'warning',
  unpaid: 'danger',
  cancelled: 'secondary',
};

type TenantPlan = TenantListItem['tenant']['plan'];

const PLAN_VARIANT: Record<TenantPlan, 'secondary' | 'info' | 'success' | 'default'> = {
  free: 'secondary',
  starter: 'info',
  pro: 'success',
  enterprise: 'default',
};

/**
 * SaaS-wide payments dashboard. Combines two endpoints:
 *   • /platform/tenants — for MRR + status counts + the "expiring soon"
 *     and "past-due" tables. Every row already includes billing info.
 *   • /platform/audit?action=tenant.record_payment — payment history.
 *
 * Strict-mode safe: both effects use a cancel flag and re-run when their
 * inputs change.
 */
export function PlatformPaymentsPage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const [tenants, setTenants] = useState<TenantListItem[] | null>(null);
  const [payments, setPayments] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tr, pr] = await Promise.all([
          platformApi().get<{ items: TenantListItem[]; total: number }>('/tenants', {
            params: { limit: 500 },
          }),
          platformApi().get<{ items: AuditEntry[]; total: number }>('/audit', {
            params: { action: 'tenant.record_payment', limit: 50 },
          }),
        ]);
        if (!cancelled) {
          setTenants(tr.data.items);
          setPayments(pr.data.items);
        }
      } catch (e) {
        if (!cancelled) setError(String((e as Error).message ?? e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- derived metrics ------------------------------------------------------

  const tenantsById = useMemo(() => {
    const m = new Map<string, TenantListItem['tenant']>();
    if (tenants) for (const r of tenants) m.set(r.tenant.id, r.tenant);
    return m;
  }, [tenants]);

  const arr = useMemo(() => {
    if (!tenants) return null;
    // Sum yearly_price across every tenant whose billing_status counts
    // as "currently paying" — Paid + Past due (still subscribed, just
    // behind). Trial / unpaid / cancelled are excluded.
    const groups = new Map<string, Decimal>(); // by currency
    for (const r of tenants) {
      if (r.tenant.billingStatus !== 'active' && r.tenant.billingStatus !== 'past_due') continue;
      if (!r.tenant.yearlyPriceAmount) continue;
      const cur = r.tenant.yearlyPriceCurrencyCode ?? 'USD';
      groups.set(cur, (groups.get(cur) ?? new Decimal(0)).plus(r.tenant.yearlyPriceAmount));
    }
    if (groups.size === 0) return { total: '0', currency: 'USD', mixed: false };
    const entries = [...groups.entries()];
    entries.sort((a, b) => b[1].comparedTo(a[1]));
    return {
      total: entries[0]![1].toFixed(2),
      currency: entries[0]![0],
      mixed: groups.size > 1,
    };
  }, [tenants]);

  const counts = useMemo(() => {
    if (!tenants) return null;
    const c = { paid: 0, trial: 0, pastDue: 0, unpaid: 0, expiringSoon: 0 };
    const horizon = Date.now() + 7 * 86_400_000;
    for (const r of tenants) {
      if (r.tenant.billingStatus === 'active') c.paid++;
      else if (r.tenant.billingStatus === 'trialing') c.trial++;
      else if (r.tenant.billingStatus === 'past_due') c.pastDue++;
      else if (r.tenant.billingStatus === 'unpaid') c.unpaid++;
      const exp = r.tenant.currentPeriodEnd ?? r.tenant.trialEndsAt;
      if (exp && new Date(exp).getTime() <= horizon) c.expiringSoon++;
    }
    return c;
  }, [tenants]);

  const paymentsThisMonth = useMemo(() => {
    if (!payments) return null;
    const monthStart = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));
    return payments.filter((p) => new Date(p.createdAt) >= monthStart).length;
  }, [payments]);

  const expiringSoon = useMemo(() => {
    if (!tenants) return null;
    const horizon = Date.now() + 14 * 86_400_000;
    return tenants
      .filter((r) => {
        const exp = r.tenant.currentPeriodEnd ?? r.tenant.trialEndsAt;
        return exp && new Date(exp).getTime() <= horizon;
      })
      .map((r) => ({
        ...r.tenant,
        expiresAt: (r.tenant.currentPeriodEnd ?? r.tenant.trialEndsAt) as string,
      }))
      .sort((a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime())
      .slice(0, 10);
  }, [tenants]);

  // --- helpers --------------------------------------------------------------

  const fmtMoney = (amount: string, currency: string): string => {
    try {
      return narrowUsd(formatNumber(Number(amount), { locale: i18n.language }, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
      }));
    } catch {
      return `${amount} ${currency}`;
    }
  };

  // --- render ---------------------------------------------------------------

  return (
    <div className="flex flex-col gap-5">
      <header>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-info/15 text-info">
            <CreditCard className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('payments.title')}</h1>
            <p className="mt-1 text-sm text-fg-muted">{t('payments.subtitle')}</p>
          </div>
        </div>
      </header>

      {error && (
        <p
          className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          role="alert"
        >
          {error}
        </p>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          tone="success"
          icon={TrendingUp}
          label={t('payments.kpi.arr')}
          value={arr ? fmtMoney(arr.total, arr.currency) : '—'}
          hint={arr?.mixed ? t('payments.kpi.arrMixed') : undefined}
          loading={!tenants}
        />
        <KpiCard
          tone="brand"
          icon={Receipt}
          label={t('payments.kpi.paymentsThisMonth')}
          value={paymentsThisMonth !== null ? String(paymentsThisMonth) : '—'}
          loading={!payments}
        />
        <KpiCard
          tone={counts && counts.expiringSoon > 0 ? 'warning' : 'neutral'}
          icon={CalendarClock}
          label={t('payments.kpi.expiringSoon')}
          value={counts ? String(counts.expiringSoon) : '—'}
          hint={t('payments.kpi.next7Days')}
          loading={!tenants}
        />
        <KpiCard
          tone={counts && counts.pastDue + counts.unpaid > 0 ? 'danger' : 'neutral'}
          icon={AlertTriangle}
          label={t('payments.kpi.behindOnPayment')}
          value={counts ? String(counts.pastDue + counts.unpaid) : '—'}
          hint={
            counts && counts.pastDue + counts.unpaid > 0
              ? `${counts.pastDue} ${t('tenants.billing.past_due').toLowerCase()} · ${counts.unpaid} ${t('tenants.billing.unpaid').toLowerCase()}`
              : undefined
          }
          loading={!tenants}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Expiring soon */}
        <Card className="p-0">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('payments.expiringSoon.title')}
            </h2>
            <p className="mt-0.5 text-xs text-fg-muted">{t('payments.expiringSoon.subtitle')}</p>
          </div>
          {!expiringSoon ? (
            <div className="p-4"><Skeleton className="h-32 w-full" /></div>
          ) : expiringSoon.length === 0 ? (
            <div className="p-6">
              <div className="flex items-center gap-2 text-sm text-fg-muted">
                <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
                {t('payments.expiringSoon.empty')}
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('payments.col.tenant')}</TableHead>
                  <TableHead>{t('payments.col.billing')}</TableHead>
                  <TableHead className="text-end">{t('payments.col.expires')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expiringSoon.map((tt) => {
                  const days = Math.ceil(
                    (new Date(tt.expiresAt).getTime() - Date.now()) / 86_400_000,
                  );
                  const overdue = days < 0;
                  return (
                    <TableRow key={tt.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <TenantAvatar logoUrl={tt.logoUrl} name={tt.name} />
                          <Link
                            to={`/platform/tenants/${tt.id}`}
                            className="hover:text-brand hover:underline underline-offset-2"
                          >
                            {tt.name}
                          </Link>
                          <Badge variant={PLAN_VARIANT[tt.plan]} className="text-[0.65rem]">
                            {t(`tenants.plans.${tt.plan}`)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={BILLING_VARIANT[tt.billingStatus]}>
                          {t(`tenants.billing.${tt.billingStatus}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-end text-xs ${overdue ? 'text-danger font-medium' : 'text-fg-muted'}`}>
                        <div>{formatDate(tt.expiresAt, { locale: i18n.language })}</div>
                        <div>
                          {overdue
                            ? t('tenants.expires.overdue', { days: -days })
                            : t('tenants.expires.inDays', { days })}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Past due / unpaid */}
        <Card className="p-0">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('payments.behind.title')}
            </h2>
            <p className="mt-0.5 text-xs text-fg-muted">{t('payments.behind.subtitle')}</p>
          </div>
          {!tenants ? (
            <div className="p-4"><Skeleton className="h-32 w-full" /></div>
          ) : (
            (() => {
              const behind = tenants.filter(
                (r) => r.tenant.billingStatus === 'past_due' || r.tenant.billingStatus === 'unpaid',
              );
              if (behind.length === 0) {
                return (
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-fg-muted">
                      <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
                      {t('payments.behind.empty')}
                    </div>
                  </div>
                );
              }
              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('payments.col.tenant')}</TableHead>
                      <TableHead>{t('payments.col.billing')}</TableHead>
                      <TableHead className="text-end">{t('payments.col.lastPayment')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {behind.map((r) => (
                      <TableRow key={r.tenant.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <TenantAvatar logoUrl={r.tenant.logoUrl} name={r.tenant.name} />
                            <Link
                              to={`/platform/tenants/${r.tenant.id}`}
                              className="hover:text-brand hover:underline underline-offset-2"
                            >
                              {r.tenant.name}
                            </Link>
                            <Badge variant={PLAN_VARIANT[r.tenant.plan]} className="text-[0.65rem]">
                              {t(`tenants.plans.${r.tenant.plan}`)}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={BILLING_VARIANT[r.tenant.billingStatus]}>
                            {t(`tenants.billing.${r.tenant.billingStatus}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-end text-xs text-fg-muted">
                          {r.tenant.lastPaymentAt
                            ? formatDate(r.tenant.lastPaymentAt, { locale: i18n.language })
                            : t('payments.never')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()
          )}
        </Card>
      </div>

      {/* Payment history */}
      <Card className="p-0">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
            {t('payments.history.title')}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t('payments.history.subtitle')}</p>
        </div>
        {!payments ? (
          <div className="p-4"><Skeleton className="h-48 w-full" /></div>
        ) : payments.length === 0 ? (
          <div className="p-6">
            <EmptyState title={t('payments.history.empty')} />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('payments.col.when')}</TableHead>
                <TableHead>{t('payments.col.tenant')}</TableHead>
                <TableHead className="text-end">{t('payments.col.years')}</TableHead>
                <TableHead>{t('payments.col.newPeriod')}</TableHead>
                <TableHead>{t('payments.col.note')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => {
                const diff = (p.diff ?? {}) as PaymentDiff;
                const t1 = p.targetTenantId ? tenantsById.get(p.targetTenantId) : null;
                const yearlyPrice = t1?.yearlyPriceAmount;
                const currency = t1?.yearlyPriceCurrencyCode ?? 'USD';
                const years = diff.years ?? 1;
                const total = yearlyPrice
                  ? fmtMoney(new Decimal(yearlyPrice).times(years).toFixed(2), currency)
                  : null;
                return (
                  <TableRow key={p.id}>
                    <TableCell className="text-xs text-fg-muted">
                      {formatDateTime(p.createdAt, { locale: i18n.language })}
                    </TableCell>
                    <TableCell className="font-medium">
                      {t1 ? (
                        <div className="flex items-center gap-2">
                          <TenantAvatar logoUrl={t1.logoUrl} name={t1.name} />
                          <Link
                            to={`/platform/tenants/${t1.id}`}
                            className="hover:text-brand hover:underline underline-offset-2"
                          >
                            {t1.name}
                          </Link>
                        </div>
                      ) : (
                        <span className="font-mono text-xs">{p.targetTenantId?.slice(0, 8) ?? '—'}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-end font-mono tabular-nums">
                      {years}
                      {total && (
                        <div className="text-xs font-normal text-fg-muted">{total}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-fg-muted">
                      {diff.after?.currentPeriodEnd
                        ? formatDate(diff.after.currentPeriodEnd, { locale: i18n.language })
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-fg-muted">
                      {diff.note || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function TenantAvatar({ logoUrl, name }: { logoUrl: string | null; name: string }): JSX.Element {
  return (
    <TenantLogo
      logoUrl={logoUrl}
      imgClassName="h-6 w-6 rounded object-cover ring-1 ring-border"
      fallback={
        <span className="flex h-6 w-6 items-center justify-center rounded bg-bg-subtle text-[0.65rem] font-semibold text-fg-muted">
          {name.charAt(0).toUpperCase()}
        </span>
      }
    />
  );
}
